import { useState } from 'react';
import styled from 'styled-components';

import { useMutation } from '@tanstack/react-query';

import Button from 'components/shared/Button';
import DatePicker from 'components/shared/form/DatePicker';
import InputGroup from 'components/shared/form/InputGroup';
import StandardModal, { Actions, PatientName } from 'components/shared/StandardModal';
import { useTranslation } from 'context/translations';
import { simpleDashDate } from 'lib/date';
import { createLocationEpisodeAdmission } from 'services/api/locationEpisodeAdmissions';

const FormContainer = styled.div`
  padding-bottom: 20px;
  display: flex;
  justify-content: center;
`;

export type AdmittedAtModalType = {
  setShow: (b: boolean) => void;
  invalidateData: () => void;
  locationEpisodeId: string;
  patientName: string;
  title?: string;
};

export default function AdmittedAtModal(props: AdmittedAtModalType) {
  const { setShow, locationEpisodeId, patientName, invalidateData, title } = props;

  const { mutate: mutateAdmission, isPending } = useMutation({
    mutationFn: createLocationEpisodeAdmission,
  });
  const { translate } = useTranslation();

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [dateError, setDateError] = useState('');
  const formattedStartDate = simpleDashDate(startDate);

  const cancel = () => {
    setShow(false);
    setStartDate(null);
    setDateError('');
  };

  const onSuccess = () => {
    invalidateData();
    cancel();
  };

  const submit = () => {
    if (!formattedStartDate) return;

    mutateAdmission({ locationEpisodeId, enteredAt: formattedStartDate }, { onSuccess });
  };

  return (
    <StandardModal title={title ?? `Confirm ${translate('Admission')} Date`} onCancel={cancel}>
      <PatientName>{patientName}</PatientName>
      <FormContainer>
        <InputGroup title={`Actual ${translate('Admission')} Date`} error={dateError} $containerWidth='auto'>
          <DatePicker
            $hasError={!!dateError}
            maxDate={new Date()}
            selected={startDate}
            onChange={(z) => {
              setDateError(z ? '' : 'This field is required');
              setStartDate(z);
            }}
          />
        </InputGroup>
      </FormContainer>
      <Actions>
        <Button variant='ghost' onClick={cancel}>
          Cancel
        </Button>
        <Button onClick={submit} disabled={!formattedStartDate} loading={isPending}>
          Confirm
        </Button>
      </Actions>
    </StandardModal>
  );
}
